import { useSyncExternalStore } from 'react';

/**
 * Field-app theme store (light / dark).
 *
 * The design tokens already define a `.dark` variant; the shell applies the
 * `dark` class to its own root so dark mode is SCOPED to the field app and
 * never bleeds into the desktop Bootstrap shell. Persisted to localStorage.
 */
export type FieldTheme = 'light' | 'dark';

const STORAGE_KEY = 'fieldtrix_theme';

function readInitial(): FieldTheme {
  if (typeof window === 'undefined') return 'light';
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

let current: FieldTheme = readInitial();
const listeners = new Set<() => void>();

export function getTheme(): FieldTheme {
  return current;
}

export function setTheme(theme: FieldTheme): void {
  if (theme === current) return;
  current = theme;
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* ignore */
  }
  listeners.forEach((fn) => fn());
}

export function toggleTheme(): void {
  setTheme(current === 'dark' ? 'light' : 'dark');
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Reactive theme + setters. */
export function useTheme(): {
  theme: FieldTheme;
  setTheme: (t: FieldTheme) => void;
  toggle: () => void;
} {
  const theme = useSyncExternalStore(subscribe, getTheme, () => 'light' as FieldTheme);
  return { theme, setTheme, toggle: toggleTheme };
}

/**
 * Theme classes for PORTALED field surfaces (drawers, dialogs, sheets). Vaul /
 * Radix render their content to `document.body`, OUTSIDE the shell's themed
 * root, so the dark tokens (`.dark`) and Tailwind dark variants (`.tw-dark`)
 * don't reach them by inheritance. Add this to every field `DrawerContent` /
 * `DialogContent` (alongside `field-surface`) so popups theme correctly.
 * Returns '' in light mode.
 */
export function useFieldThemeClass(): string {
  const theme = useSyncExternalStore(subscribe, getTheme, () => 'light' as FieldTheme);
  return theme === 'dark' ? 'dark tw-dark' : '';
}
